"use client";

import React, { useState, useEffect } from 'react';
import { Shield, Bug, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Gift, Trophy, Target, DollarSign, CheckCircle2, HelpCircle, Mail, Zap, Lock, Star } from 'lucide-react';
import Link from 'next/link';
import { motion } from 'motion/react';

import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { AnimatedSection, AnimatedCard, AnimatedIcon, AnimatedFeature } from '../../components/AnimatedComponents';

export default function BugBountyPage() {
  const [showFaq1, setShowFaq1] = useState(false);
  const [showFaq2, setShowFaq2] = useState(false);
  const [showFaq3, setShowFaq3] = useState(false);
  const [showFaq4, setShowFaq4] = useState(false);
  const [showFaq5, setShowFaq5] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    // Set animation complete after initial load animations
    const timer = setTimeout(() => {
      setAnimationComplete(true);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  const securityLevels = [
    {
      name: "Low Risk",
      description: "Minor security issues with limited impact on user data or system functionality.",
      reward: "$50 - $200",
      icon: Zap,
      color: "from-green-400 to-blue-500",
      examples: ["Information disclosure", "Minor authentication bypass", "Low-impact XSS"]
    },
    {
      name: "Medium Risk",
      description: "Moderate security vulnerabilities that could potentially compromise user data.",
      reward: "$200 - $1,000",
      icon: AlertTriangle,
      color: "from-yellow-300 to-yellow-500",
      examples: ["SQL injection", "CSRF attacks", "Privilege escalation"]
    },
    {
      name: "High Risk",
      description: "Serious security flaws that could lead to significant data breaches or system compromise.",
      reward: "$1,000 - $5,000",
      icon: Shield,
      color: "from-orange-400 to-red-500",
      examples: ["Remote code execution", "Authentication bypass", "Data breach scenarios"]
    },
    {
      name: "Critical Risk",
      description: "Severe vulnerabilities that pose immediate threats to user safety and platform integrity.",
      reward: "$5,000 - $20,000",
      icon: Lock,
      color: "from-red-500 to-purple-600",
      examples: ["Complete system compromise", "Mass data exposure", "Account takeover at scale"]
    }
  ];

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { type: "spring" as const, stiffness: 70 }
    }
  };

  const heroMotion = {
    initial: { opacity: 0, scale: 0.9 },
    animate: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        type: "spring" as const,
        stiffness: 100,
        damping: 20,
        duration: 0.8
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="absolute inset-0 bg-linear-to-br from-blue-50 via-white to-blue-100 pointer-events-none -z-10 opacity-50"
      />
      
      {/* Decorative Elements - Reduce opacity and position more carefully */}
      <motion.div 
        className="absolute top-[5%] right-[10%] w-64 h-64 rounded-full bg-linear-to-br from-blue-200/10 to-blue-500/5 blur-3xl pointer-events-none"
        animate={{ 
          scale: [1, 1.2, 1],
          rotate: [0, 10, 0],
          opacity: [0.2, 0.3, 0.2]
        }}
        transition={{ 
          duration: 15,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      
      <motion.div 
        className="absolute bottom-[30%] left-[5%] w-56 h-56 rounded-full bg-linear-to-tr from-blue-300/10 to-purple-500/5 blur-3xl pointer-events-none"
        animate={{ 
          scale: [1, 1.1, 1],
          rotate: [0, -10, 0],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{ 
          duration: 12,
          repeat: Infinity,
          ease: "linear",
          delay: 1
        }}
      />
      
      <main className="grow relative z-10">
        {/* Hero Section */}
        <motion.div 
          {...heroMotion}
          className="py-12 lg:py-20 mb-8 relative overflow-hidden"
        >
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}  
                className="bg-linear-to-br from-blue-600 to-blue-800 p-4 rounded-full inline-flex mb-6 shadow-lg"
              >
                <Bug className="h-10 w-10 text-white" />
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 bg-clip-text text-transparent bg-linear-to-r from-blue-600 to-blue-800"
              >
                Bug Bounty Program
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="text-lg text-gray-600 mb-6 max-w-3xl mx-auto"
              >
                  Help us improve RentWise's security and get rewarded for your findings. 
                  We value the security research community's efforts to help protect our users.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.9 }}
              >
                <Button size="lg" className="bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white shadow-md">
                  <Link href="/vulnerability" className="flex items-center gap-2">
                    Report a Vulnerability
                    <ChevronDown className="h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
        
        <div className="container mx-auto px-4 mb-16 relative z-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <AnimatedSection
                variants={fadeInUp}
                className="mb-8"
              >
                <Card className="overflow-hidden border shadow-md bg-white">
                  <div className="absolute inset-0 bg-linear-to-br from-blue-100/30 to-white pointer-events-none opacity-70" />
                  <div className="relative z-10">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-2xl">
                        <AnimatedIcon>
                          <Trophy className="h-6 w-6 text-blue-600" />
                        </AnimatedIcon>
                        Program Overview
                      </CardTitle>
                      <CardDescription className="text-base">
                        Find security vulnerabilities in our systems and get rewarded for responsible disclosure.
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="space-y-6">
                      <p className="text-gray-700">
                        At RentWise, we take the security of our users and platform very seriously. Our Bug Bounty Program 
                        is designed to encourage security researchers to report security vulnerabilities responsibly.
                      </p>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        {securityLevels.map((level, index) => (
                          <motion.div
                            key={index}
                            variants={fadeInUp}
                            className="bg-white p-6 rounded-xl shadow-md border-l-4 border-primary"
                          >
                            <div className="flex items-center mb-3">
                              <div className={`p-2 rounded-lg bg-linear-to-r ${level.color} text-white mr-3`}>
                                <level.icon className="h-5 w-5" />
                              </div>
                              <h3 className="text-lg font-semibold">{level.name}</h3>
                            </div>
                            <p className="text-gray-600 mb-3">{level.description}</p>
                            <div className="bg-gray-50 p-3 rounded-lg mb-3">
                              <p className="text-sm font-medium text-gray-800">Reward Range:</p>
                              <p className="text-xl font-bold text-primary">{level.reward}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800 mb-2">Examples:</p>
                              <ul className="text-sm text-gray-600 space-y-1">
                                {level.examples.map((example, i) => (
                                  <li key={i} className="flex items-center">
                                    <div className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></div>
                                    {example}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                      
                      <Alert className="bg-blue-50 border border-blue-200 shadow-sm">
                        <AlertTriangle className="h-5 w-5 text-blue-600" />
                        <AlertTitle className="text-blue-800 text-base">Important</AlertTitle>
                        <AlertDescription className="text-blue-800">
                          Always test in a way that does not impact real users or their data. Testing should be conducted in a responsible manner.
                        </AlertDescription>
                      </Alert>
                    </CardContent>
                  </div>
                </Card>
              </AnimatedSection>
              
              <AnimatedSection
                variants={fadeInUp}
                className="mb-8"
              >
                <Card className="overflow-hidden border shadow-md bg-white">
                  <div className="absolute inset-0 bg-linear-to-br from-blue-100/30 to-white pointer-events-none opacity-70" />
                  <div className="relative z-10">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-2xl">
                        <AnimatedIcon>
                          <DollarSign className="h-6 w-6 text-blue-600" />
                        </AnimatedIcon>
                        Bounty Rewards
                      </CardTitle>
                      <CardDescription className="text-base">
                        We offer rewards based on the severity and impact of the vulnerability you discover.
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="space-y-6">
                      <p className="text-gray-700">
                        Our bounty rewards are determined by the severity of the vulnerability and the potential impact 
                        it could have on our users and systems. The final reward amount is at the discretion of our 
                        security team after thorough evaluation.
                      </p>
                      
                      <motion.div 
                        variants={staggerContainer}
                        initial="hidden"
                        animate="visible"
                        className="space-y-5"
                      >
                        {securityLevels.map((level, index) => (
                          <AnimatedCard key={index}>
                            <motion.div 
                              className={`border rounded-xl p-5 shadow-sm overflow-hidden relative bg-white`}
                              whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
                            >
                              <div className="relative z-10">
                                <div className="flex justify-between items-center mb-3">
                                  <h3 className="font-bold text-lg flex items-center gap-2">
                                    <level.icon className="h-5 w-5" />
                                    {level.name}
                                  </h3>
                                  <Badge variant="outline" className="border-primary text-primary px-3 py-1 text-sm font-semibold shadow-sm">
                                    {level.reward}
                                  </Badge>
                                </div>
                                <p className="mb-3 text-sm">{level.description}</p>
                                <div className="text-sm">
                                  <span className="font-medium">Examples: </span>
                                  {level.examples.join(', ')}
                                </div>
                              </div>
                            </motion.div>
                          </AnimatedCard>
                        ))}
                      </motion.div>
                      
                      <AnimatedCard>
                        <div className="bg-linear-to-br from-purple-50 to-indigo-50 p-5 rounded-xl border border-purple-100 shadow-sm">
                          <h3 className="font-medium mb-2 flex items-center gap-2">
                            <Gift className="h-5 w-5 text-purple-600" />
                            Additional Rewards
                          </h3>
                          <p className="text-sm">
                            Exceptional reports with detailed information, proof of concept code, and suggested fixes may 
                            receive bonuses beyond the standard reward amounts. We also recognize top contributors on our 
                            security hall of fame.
                          </p>
                        </div>
                      </AnimatedCard>
                    </CardContent>
                  </div>
                </Card>
              </AnimatedSection>
              
              <AnimatedSection
                variants={fadeInUp}
                className="mb-8"
              >
                <Card className="overflow-hidden border shadow-md bg-white">
                  <div className="absolute inset-0 bg-linear-to-br from-blue-100/30 to-white pointer-events-none opacity-70" />
                  <div className="relative z-10">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-2xl">
                        <AnimatedIcon>
                          <Target className="h-6 w-6 text-blue-600" />
                        </AnimatedIcon>
                        Submission Process
                      </CardTitle>
                      <CardDescription className="text-base">
                        How to report vulnerabilities and what information to include
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="space-y-6">
                      <motion.div 
                        variants={staggerContainer}
                        initial="hidden"
                        animate="visible"  
                        className="space-y-5"
                      >
                        <AnimatedFeature className="flex items-start">
                          <div className="shrink-0 bg-linear-to-br from-blue-500 to-blue-600 text-white rounded-full h-10 w-10 flex items-center justify-center mr-4 shadow-md">
                            1
                          </div>
                          <div>
                            <h3 className="font-medium text-lg mb-1">Discover a vulnerability</h3>
                            <p className="text-gray-600">
                              Find a security issue in one of our in-scope systems or applications.
                            </p>
                          </div>
                        </AnimatedFeature>
                        
                        <AnimatedFeature className="flex items-start">
                          <div className="shrink-0 bg-linear-to-br from-blue-500 to-blue-600 text-white rounded-full h-10 w-10 flex items-center justify-center mr-4 shadow-md">
                            2
                          </div>
                          <div>
                            <h3 className="font-medium text-lg mb-1">Report via our secure channel</h3>
                            <p className="text-gray-600">
                              Submit your findings through our <Link href="/vulnerability" className="text-blue-600 hover:underline font-medium">vulnerability report form</Link> or email <span className="font-medium">security@rentwise.com</span> with encrypted details.
                            </p>
                          </div>
                        </AnimatedFeature>
                        
                        <AnimatedFeature className="flex items-start">
                          <div className="shrink-0 bg-linear-to-br from-blue-500 to-blue-600 text-white rounded-full h-10 w-10 flex items-center justify-center mr-4 shadow-md">
                            3
                          </div>
                          <div>
                            <h3 className="font-medium text-lg mb-1">Evaluation and confirmation</h3>
                            <p className="text-gray-600">
                              Our security team will review your report, confirm the vulnerability, and determine its severity.
                            </p>
                          </div>
                        </AnimatedFeature>
                        
                        <AnimatedFeature className="flex items-start">
                          <div className="shrink-0 bg-linear-to-br from-blue-500 to-blue-600 text-white rounded-full h-10 w-10 flex items-center justify-center mr-4 shadow-md">
                            4
                          </div>
                          <div>
                            <h3 className="font-medium text-lg mb-1">Remediation and reward</h3>
                            <p className="text-gray-600">
                              We'll fix the issue and award you based on the severity and impact of the finding.
                            </p>
                          </div>
                        </AnimatedFeature>
                      </motion.div>
                      
                      <div className="bg-white border-2 border-blue-100 rounded-xl p-6 shadow-md mt-6">
                        <h3 className="font-semibold text-lg mb-4 text-blue-700 flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5" />
                          Required Information
                        </h3>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3 mt-0.5">
                              <CheckCircle className="h-3 w-3" />
                            </span>
                            <span className="text-gray-800">Clear description of the vulnerability</span>
                          </li>
                          <li className="flex items-start">
                            <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3 mt-0.5">
                              <CheckCircle className="h-3 w-3" />
                            </span>
                            <span className="text-gray-800">Step-by-step reproduction instructions</span>
                          </li>
                          <li className="flex items-start">
                            <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3 mt-0.5">
                              <CheckCircle className="h-3 w-3" />
                            </span>
                            <span className="text-gray-800">Screenshots or videos demonstrating the issue</span>
                          </li>
                          <li className="flex items-start">
                            <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3 mt-0.5">
                              <CheckCircle className="h-3 w-3" />
                            </span>
                            <span className="text-gray-800">Affected URLs or components</span>
                          </li>
                          <li className="flex items-start">
                            <span className="bg-blue-100 text-blue-600 rounded-full p-1 mr-3 mt-0.5">
                              <CheckCircle className="h-3 w-3" />
                            </span>
                            <span className="text-gray-800">Possible impact and suggestions for mitigation</span>
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </AnimatedSection>
              
              <AnimatedSection variants={fadeInUp}>
                <Card className="overflow-hidden border shadow-md bg-white">
                  <div className="absolute inset-0 bg-linear-to-br from-blue-100/30 to-white pointer-events-none opacity-70" />
                  <div className="relative z-10">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-2xl">
                        <AnimatedIcon>
                          <HelpCircle className="h-6 w-6 text-blue-600" />
                        </AnimatedIcon>
                        Frequently Asked Questions
                      </CardTitle>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {[
                        {
                          question: "How long does it take to process my report?",
                          answer: "We aim to acknowledge receipt of your report within 24 hours and provide an initial assessment within 3-5 business days. Complex issues may take longer to fully investigate.",
                          state: showFaq1,
                          setState: setShowFaq1
                        },
                        {
                          question: "Can I publish my findings after you fix the issue?",
                          answer: "Yes, we support responsible disclosure. We ask that you wait until we've fixed the issue and notify you that it's resolved. We typically request a 90-day disclosure timeline from the date of your report.",
                          state: showFaq2,
                          setState: setShowFaq2
                        },
                        {
                          question: "How are reward amounts determined?",
                          answer: "Reward amounts are based on the severity of the vulnerability, potential impact, quality of the report, and difficulty of exploitation. Our security team makes the final determination after careful evaluation.",
                          state: showFaq3,
                          setState: setShowFaq3
                        },
                        {
                          question: "Do you have a safe harbor policy?",
                          answer: "Yes, we provide safe harbor for security researchers who discover and report vulnerabilities according to our responsible disclosure policy. This means we won't pursue legal action for security research conducted in good faith and in compliance with our guidelines.",
                          state: showFaq4,
                          setState: setShowFaq4
                        },
                        {
                          question: "Can I report vulnerabilities anonymously?",
                          answer: "Yes, we accept anonymous reports. However, if you want to receive a bounty reward, we'll need some form of contact information to process the payment. We respect your privacy and handle all information confidentially.",
                          state: showFaq5,
                          setState: setShowFaq5
                        }
                      ].map((faq, index) => (
                        <AnimatedCard key={index}>
                          <motion.div 
                            className="border rounded-xl overflow-hidden shadow-sm"
                            whileHover={{ y: -2 }}
                          >
                            <button
                              onClick={() => faq.setState(!faq.state)}
                              className="w-full px-5 py-4 text-left font-medium flex justify-between items-center hover:bg-slate-50/90 bg-white"
                            >
                              <span>{faq.question}</span>
                              <motion.div
                                animate={{ rotate: faq.state ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <ChevronDown size={18} />
                              </motion.div>
                            </button>
                            
                            <motion.div 
                              initial={false}
                              animate={{ 
                                height: faq.state ? 'auto' : 0,
                                opacity: faq.state ? 1 : 0
                              }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="px-5 py-4 bg-linear-to-br from-gray-50 to-slate-50 text-gray-700">
                                {faq.answer}
                              </div>
                            </motion.div>
                          </motion.div>
                        </AnimatedCard>
                      ))}
                    </CardContent>
                  </div>
                </Card>
              </AnimatedSection>
            </div>
            
            <div className="lg:col-span-1">
              <div className="sticky top-6">
                <AnimatedSection
                  variants={fadeInUp}
                  className="mb-6"
                >
                  <Card className="overflow-hidden border shadow-md bg-white">
                    <div className="absolute inset-0 bg-linear-to-br from-blue-100/30 to-white pointer-events-none opacity-70" />
                    <div className="relative z-10">
                      <CardHeader>
                        <CardTitle className="text-xl flex items-center gap-2">
                          <Mail className="h-5 w-5 text-blue-500" />
                          Contact Us
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm mb-5 text-gray-600">
                          Have questions about our bug bounty program? Contact our security team:
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                            <Mail className="h-5 w-5 mr-3 text-blue-500" />
                            <span className="font-medium">security@rentwise.com</span>
                          </div>
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <Button variant="default" className="w-full bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-primary-hover)] text-white shadow-md">
                              <Link href="/vulnerability" className="flex items-center justify-center w-full gap-2">
                                Report a Vulnerability
                                <ChevronDown className="h-4 w-4" />
                              </Link>
                            </Button>
                          </motion.div>
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                </AnimatedSection>
                
                <AnimatedSection variants={fadeInUp}>
                  <Card className="overflow-hidden border shadow-md bg-white">
                    <div className="absolute inset-0 bg-linear-to-br from-blue-100/30 to-white pointer-events-none opacity-70" />
                    <div className="relative z-10">
                      <CardHeader className="pb-2">
                        <motion.div 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5 }}
                          className="flex flex-col items-center mb-2"
                        >
                          <motion.div
                            animate={{ 
                              scale: [1, 1.05, 1],
                              rotate: [0, 2, 0, -2, 0]
                            }}
                            transition={{ 
                              duration: 3, 
                              repeat: Infinity, 
                              ease: "easeInOut" 
                            }}
                            className="p-3 bg-linear-to-br from-yellow-200 to-yellow-400 rounded-full mb-3 shadow-lg"
                          >
                            <Trophy className="h-6 w-6 text-white" />
                          </motion.div>
                          <CardTitle className="text-xl text-center bg-clip-text text-transparent bg-linear-to-br from-blue-700 via-blue-600 to-purple-700 font-bold">
                            Security Hall of Fame
                          </CardTitle>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.5 }}
                        >
                          <div className="h-1 w-16 bg-linear-to-r from-blue-500 to-purple-500 rounded-full mx-auto"></div>
                        </motion.div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm mb-5 text-gray-600 text-center">
                          We celebrate security researchers who have helped
                          protect RentWise and our users.
                        </p>
                        <motion.ul 
                          variants={staggerContainer}
                          initial="hidden"
                          animate="visible"
                          className="space-y-3 mt-4"
                        >
                          {[
                            { 
                              name: "Jane Smith", 
                              achievement: "3 critical vulnerabilities", 
                              rank: "1", 
                              badge: "🏆 Top Contributor",
                              badgeColor: "bg-linear-to-r from-yellow-300 to-yellow-400"
                            },
                            { 
                              name: "John Doe", 
                              achievement: "2 high severity issues", 
                              rank: "2", 
                              badge: "🔍 Security Expert",
                              badgeColor: "bg-linear-to-r from-indigo-500 to-blue-500"
                            },
                            { 
                              name: "Alex Johnson", 
                              achievement: "Multiple security improvements", 
                              rank: "3", 
                              badge: "⚡ Rising Star",
                              badgeColor: "bg-linear-to-r from-emerald-500 to-teal-500"
                            },
                            { 
                              name: "Sarah Williams", 
                              achievement: "API security specialist", 
                              rank: "4", 
                              badge: "🛡️ Specialist",
                              badgeColor: "bg-linear-to-r from-violet-500 to-purple-500"
                            }
                          ].map((researcher, index) => (
                            <motion.li 
                              key={index}
                              variants={fadeInUp}
                              whileHover={{ scale: 1.02, x: 5 }}
                              className="border border-gray-100 rounded-xl overflow-hidden bg-white shadow-sm transition-all duration-300 hover:shadow-md"
                            >
                              <div className="flex items-center p-3">
                                <div className="mr-3 relative">
                                  <div className="rounded-full bg-linear-to-br from-blue-200 to-blue-100 h-12 w-12 flex items-center justify-center text-blue-700 font-medium shadow-sm">
                                    {researcher.name.charAt(0)}
                                  </div>
                                  <div className="absolute -bottom-1 -right-1 bg-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center border border-gray-200 shadow-sm">
                                    {researcher.rank}
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <div className="font-bold text-gray-800">{researcher.name}</div>
                                  <div className="text-xs text-gray-600">{researcher.achievement}</div>
                                  <div className={`text-xs mt-1.5 px-2 py-0.5 rounded-full text-white inline-block text-xs ${researcher.badgeColor}`}>
                                    {researcher.badge}
                                  </div>
                                </div>
                                <motion.div 
                                  whileHover={{ rotate: 10 }}
                                  whileTap={{ scale: 0.95 }}
                                  className="text-blue-500 cursor-pointer"
                                >
                                  <Star className="h-5 w-5" />
                                </motion.div>
                              </div>
                            </motion.li>
                          ))}
                        </motion.ul>
                        <motion.div 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.8 }}
                          className="mt-6 text-center"
                        >
                          <Link 
                            href="#" 
                            className="group inline-flex items-center px-4 py-2 rounded-full bg-linear-to-r from-blue-600 to-blue-700 text-white text-sm hover:shadow-md transition-all duration-300 hover:translate-y-[-2px] font-medium"
                          >
                            <span>View Full Leaderboard</span>
                            <motion.span
                              animate={{ x: [0, 4, 0] }}
                              transition={{ duration: 1.5, repeat: Infinity, repeatType: "mirror" }}
                              className="ml-1.5"
                            >
                              <ChevronDown className="h-4 w-4 -rotate-90" />
                            </motion.span>
                          </Link>
                        </motion.div>
                      </CardContent>
                    </div>
                  </Card>
                </AnimatedSection>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
} 